/**
 * How the newly shown panel's content arrives: `in` rises into place (first
 * open), `l`/`r` slide in from the side of travel when switching between two
 * panels that are both open, taken from their order in the nav.
 */
export type NavPanelMotion = 'in' | 'l' | 'r';

export type NavPanelSnapshot = {
  /** The panel that is open, or null while closed (and while closing). */
  readonly openId: string | null;
  /** The panel whose content is shown. Lags `openId` on close, so the panel collapses with its content visible. */
  readonly shownId: string | null;
  readonly motion: NavPanelMotion;
};

/** Every delay the panel's hover and click behaviour turns on, in milliseconds. */
export const NAV_PANEL_TIMING = {
  /** Hover intent: how long the pointer must rest on a trigger before a closed panel opens. */
  intent: 80,
  /** Grace period after the pointer leaves the header (bar and panel) before the panel closes. */
  leave: 220,
  /** Delay before an open panel closes once the pointer is on a plain nav link. */
  plainItem: 120,
  /** How long the content stays shown after closing: the height collapse (300ms) plus slack. */
  hide: 400,
  /** A click this soon after hover opened the panel is the same gesture, not a request to close it. */
  clickGuard: 400,
} as const;

type TimerName = 'intent' | 'close' | 'hide';

const CLOSED: NavPanelSnapshot = { openId: null, shownId: null, motion: 'in' };

/**
 * The open/close state of one header's full-width nav panels, with the timers
 * that make hover feel deliberate. A plain store rather than React state so
 * the timing rules can be exercised with fake timers and no rendering; the
 * client shell reads it through `useSyncExternalStore`.
 */
export class NavPanelController {
  private snapshot: NavPanelSnapshot = CLOSED;
  private readonly listeners = new Set<() => void>();
  private readonly timers: Partial<Record<TimerName, ReturnType<typeof setTimeout>>> = {};
  private openedAt = Number.NEGATIVE_INFINITY;
  private order: readonly string[];
  private readonly now: () => number;

  constructor(order: readonly string[], now: () => number = Date.now) {
    this.order = order;
    this.now = now;
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  readonly getSnapshot = (): NavPanelSnapshot => this.snapshot;

  /** The panels' ids in nav order, which decides the slide direction when switching. */
  setOrder(order: readonly string[]): void {
    this.order = order;
  }

  /** Opens `id` now, switching from any open panel. */
  show(id: string): void {
    this.clear('intent');
    this.clear('close');
    this.clear('hide');

    const { openId, shownId } = this.snapshot;
    if (openId === id) { return; }

    const motion: NavPanelMotion = openId !== null && shownId !== null
      ? (this.order.indexOf(id) > this.order.indexOf(shownId) ? 'r' : 'l')
      : 'in';

    this.openedAt = this.now();
    this.set({ openId: id, shownId: id, motion });
  }

  /** Closes the open panel now; its content stays shown until the collapse has finished. */
  close(): void {
    this.clear('intent');
    this.clear('close');
    if (this.snapshot.openId === null) { return; }

    this.set({ ...this.snapshot, openId: null });
    this.timers.hide = setTimeout(() => {
      delete this.timers.hide;
      this.set(CLOSED);
    }, NAV_PANEL_TIMING.hide);
  }

  /** The pointer came to rest on a trigger: switch at once while a panel is open, else open after the intent delay. */
  hoverTrigger(id: string): void {
    this.clear('intent');
    this.clear('close');
    if (this.snapshot.openId !== null) {
      this.show(id);
      return;
    }
    this.timers.intent = setTimeout(() => {
      delete this.timers.intent;
      this.show(id);
    }, NAV_PANEL_TIMING.intent);
  }

  /** A click (or Enter/Space) on a trigger toggles its panel, ignoring the click that ends a hover-open. */
  clickTrigger(id: string): void {
    this.clear('intent');
    if (this.snapshot.openId !== id) {
      this.show(id);
      return;
    }
    if (this.now() - this.openedAt < NAV_PANEL_TIMING.clickGuard) { return; }
    this.close();
  }

  /** The pointer left a trigger before its intent delay ran out: it was passing over, so nothing opens. */
  cancelIntent(): void {
    this.clear('intent');
  }

  /** The pointer is on a plain nav link: an open panel gives way shortly after. */
  hoverPlainItem(): void {
    this.clear('intent');
    if (this.snapshot.openId === null || this.timers.close !== undefined) { return; }
    this.schedule('close', NAV_PANEL_TIMING.plainItem);
  }

  /** The pointer is back inside the header, or on the panel itself: cancel any pending close. */
  cancelClose(): void {
    this.clear('close');
  }

  /** The pointer left the header (bar and panel). */
  leave(): void {
    this.clear('intent');
    if (this.snapshot.openId === null) { return; }
    this.clear('close');
    this.schedule('close', NAV_PANEL_TIMING.leave);
  }

  /** Cancels every pending timer (the shell unmounted). Subscribers are left to unsubscribe themselves. */
  dispose(): void {
    this.clear('intent');
    this.clear('close');
    this.clear('hide');
  }

  private schedule(name: 'close', ms: number): void {
    this.timers[name] = setTimeout(() => {
      delete this.timers[name];
      this.close();
    }, ms);
  }

  private clear(name: TimerName): void {
    const timer = this.timers[name];
    if (timer === undefined) { return; }
    clearTimeout(timer);
    delete this.timers[name];
  }

  private set(next: NavPanelSnapshot): void {
    this.snapshot = next;
    this.listeners.forEach((listener) => listener());
  }
}
